import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { screenApi } from '../screen/api.js';
import { defaultBidForDeal } from '../engine/deskBid.js';
import { buildForecastCases } from './model.js';
import { useMeetingDesk } from './deskState.js';
import { useAssistantPageExtras } from '../components/assistant/AssistantContext';

export function Loading({ label = 'Loading…' }) {
  return (
    <div className="mp-loading" role="status">
      <div className="loading-spinner" />
      <span>{label}</span>
    </div>
  );
}

export function Src({ kind }) {
  const label = kind === 'seller' ? 'SELLER' : kind === 'model' ? 'MODEL' : 'JUDGMENT';
  return <span className={`mp-src ${kind}`}>{label}</span>;
}

export function SourceKey() {
  return (
    <div className="mp-key">
      <span>
        <Src kind="seller" /> from the data room
      </span>
      <span>
        <Src kind="model" /> D2D calculation
      </span>
      <span>
        <Src kind="judgment" /> desk assumption
      </span>
    </div>
  );
}

export function NextStep({ label, question, onClick }) {
  return (
    <div className="mp-callout">
      <div className="k">Next in the meeting</div>
      <div className="t">{question}</div>
      <div>
        <button type="button" className="mp-btn amber" onClick={onClick}>
          {label} →
        </button>
      </div>
    </div>
  );
}

/**
 * Shared per-deal meeting state: desk knobs + forecast cases (built once from
 * the DealPackage and the desk's own case). Every step reads the same numbers.
 */
export function useMeeting({ dealId, dealPackage }) {
  const [rawDesk, patchRaw] = useMeetingDesk(dealId);
  const patchDesk = useCallback((patch) => {
    patchRaw((prev) => {
      const obj = typeof patch === 'function' ? patch(prev) : patch;
      if (obj && Object.prototype.hasOwnProperty.call(obj, 'bidPriceMM')) {
        return { ...obj, bidEdited: true };
      }
      return obj;
    });
  }, [patchRaw]);
  // ARO: the desk's own entry wins; otherwise the configured ARO (D2D deck /
  // overrides) merged into the package by withAroEstimate; otherwise unset.
  const pkgAro = dealPackage?.assumptions?.aroMM;
  const deskAro = typeof rawDesk.aroMM === 'number' && Number.isFinite(rawDesk.aroMM) ? rawDesk.aroMM : null;
  const pkgAroSet = typeof pkgAro === 'number' && Number.isFinite(pkgAro) && pkgAro > 0;
  const resolvedBid = defaultBidForDeal(dealId, dealPackage, {
    sellerAskMM: dealPackage?.meta?.sellerAskMM ?? null,
    storedBid: rawDesk.bidPriceMM,
    bidEdited: rawDesk.bidEdited === true,
  });
  const desk = useMemo(
    () => ({
      ...rawDesk,
      bidPriceMM: resolvedBid.bidPriceMM,
      bidDefaultLabel: resolvedBid.label,
      aroMM: deskAro != null ? deskAro : pkgAroSet ? pkgAro : 0,
      // An explicit desk entry (including a real $0) counts as set; 0 alone does not.
      aroSet: deskAro != null || pkgAroSet,
      aroSource: deskAro != null ? 'desk' : pkgAroSet ? dealPackage?.assumptions?.aroSource || 'configured ARO' : null,
    }),
    [rawDesk, deskAro, pkgAroSet, pkgAro, dealPackage, resolvedBid.bidPriceMM, resolvedBid.label],
  );
  const forecast = useMemo(
    () => (dealPackage ? buildForecastCases(dealPackage, desk.custom) : null),
    [dealPackage, desk.custom],
  );
  return { desk, patchDesk, forecast };
}

/** Publish a compact page summary to the desk assistant. */
export function useAssistantSummary(summary) {
  const { setPageExtras, clearPageExtras } = useAssistantPageExtras();
  const json = JSON.stringify(summary || null);
  useEffect(() => {
    if (!summary) return undefined;
    setPageExtras(summary);
    return () => clearPageExtras();
    // json is the stable dependency for the summary object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [json, setPageExtras, clearPageExtras]);
}

/** Latest server quick-screen view for one deal ({ result, running, stale }) or null. */
export function useServerScreen(dealId) {
  const [view, setView] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setView(null);
    if (!dealId) return undefined;
    screenApi
      .deal(dealId)
      .then((v) => {
        if (!cancelled) setView(v);
      })
      .catch(() => {
        if (!cancelled) setView(null);
      });
    return () => {
      cancelled = true;
    };
  }, [dealId]);
  return view;
}
